import { StorageService } from './storage-service';
import { VidebisTest } from '../util/types';
import { GammaCorrectionLogic } from '../../eye-test/logic/gamma-correction-logic';

export class GlobalLogic {

	private _height: number | null;
	private _yearOfBirth: number | null;
	private _scaleFactor: number;
	private _distance: number;
	private readonly _finishedTests: VidebisTest[];
	private _storageService: StorageService;
	private _allTestsMode: boolean;
	private _gammaCorrectionLogic = GammaCorrectionLogic.getInstance();

	private static _instance: GlobalLogic | null = null;

	static getInstance(): GlobalLogic {
		if (!GlobalLogic._instance) {
			GlobalLogic._instance = new GlobalLogic();
		}
		return GlobalLogic._instance;
	}

	get yearOfBirth(): number | null {
		const savedAge: string | null = this._storageService.get('yearOfBirth');
		if (!savedAge) {
			return this._yearOfBirth;
		}
		if (isNaN(+savedAge)) {
			this._storageService.del(['yearOfBirth']);
			return this._yearOfBirth;
		}
		return +savedAge;
	}

	set yearOfBirth(age: number | null) {
		if (!age) {
			this._storageService.del(['yearOfBirth']);
		} else {
			this._storageService.set('yearOfBirth', age.toString());
		}
		this._yearOfBirth = age;
	}

	get scaleFactor(): number {
		const savedScaleFactor: string | null = this._storageService.get('scaleFactor');
		if (!savedScaleFactor) return this._scaleFactor;
		if (isNaN(+savedScaleFactor)) {
			this._storageService.del(['scaleFactor']);
			return this._scaleFactor;
		}
		return +savedScaleFactor;
	}

	set scaleFactor(scaleFactor: number) {
		this._storageService.set('scaleFactor', scaleFactor.toString());
		this._scaleFactor = scaleFactor;
	}

	get distance(): number {
		const savedDistance: string | null = this._storageService.get('distance');

		if (!savedDistance) return this._distance;
		if (isNaN(+savedDistance)) {
			this._storageService.del(['distance']);
			return this._distance;
		}
		return +savedDistance;
	}

	set distance(bodyHeight: number) {
		// const scaledDistance: number = bodyHeight / 2.4; // old scaling
		const scaledDistance: number = bodyHeight / 3;
		this._storageService.set('distance', scaledDistance.toString());
		this._storageService.set('height', bodyHeight);
		this._height = bodyHeight;
		this._distance = scaledDistance;
	}

	get height(): number | null {
		const savedHeight: string | null = this._storageService.get('height');
		if (!savedHeight) {
			return this._height;
		}
		if (isNaN(+savedHeight)) {
			this._storageService.del(['height']);
			return this._height;
		}
		return +savedHeight;
	}

	get allTestsMode(): boolean {
		const storedValue = this._storageService.get('allTestsMode') as boolean;
		if (storedValue === null) {
			return this._allTestsMode;
		}
		return storedValue;
	}

	set allTestsMode(val: boolean) {
		this._allTestsMode = val;
		this._storageService.set('allTestsMode', val);
	}

	markTestAsFinished(test: VidebisTest): void {
		if (!this._finishedTests.includes(test)) {
			this._finishedTests.push(test);
			this._storageService.set('videbisTestsFinished', JSON.stringify(this._finishedTests));
		}
	}

	removeTestFromFinishedList(test: VidebisTest): void {
		if (!this._finishedTests.includes(test)) {
			return;
		}

		const index = this._finishedTests.indexOf(test);
		this._finishedTests.splice(index, 1);
		this._storageService.set('videbisTestsFinished', JSON.stringify(this._finishedTests));
	}

	getTelemetryData(): any {
		return {
			scaleFactor: +this.scaleFactor.toFixed(4),
			screenDistance: this.distance !== -1 ? +this.distance.toFixed(4) : null,
			calibrationColor: this._gammaCorrectionLogic.getCurrentColor(),
			gammaFactor: this._gammaCorrectionLogic.getGammaCorrection().toFixed(4),
			yearOfBirth: this.yearOfBirth,
			height: this.height
		}
	}

	private constructor() {
		this._scaleFactor = 1;
		this._height = null;
		this._yearOfBirth = null;
		this._distance = -1;
		this._allTestsMode = false;
		this._storageService = StorageService.getInstance();
		const buf = this._storageService.get('videbisTestsFinished');
		if (buf) {
			this._finishedTests = JSON.parse(buf) as VidebisTest[];
		} else {
			this._finishedTests = [];
		}
	}

}
